import {
  makeStyles, shorthands, tokens, Text, Button, Spinner,
  Popover, PopoverTrigger, PopoverSurface, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { Info20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';

const useStyles = makeStyles({
  body: {
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM,
    maxWidth: '460px', maxHeight: '70vh', overflowY: 'auto',
    ...shorthands.padding(tokens.spacingVerticalS),
  },
  label: {
    color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  term: {
    display: 'grid', gridTemplateColumns: 'minmax(74px, auto) 1fr',
    columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXS,
  },
  key: { fontWeight: tokens.fontWeightSemibold, fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/**
 * Explains how to read a framework.
 *
 * An analyst meeting a framework for the first time needs to know what IG1
 * means, or why an ISM control is marked P, before the control list is any use
 * at all. Without it the labels are noise.
 *
 * This describes structure in our own words rather than restating control text,
 * which is what the publishers' licences are concerned with.
 */
export function FrameworkGuide({ frameworkId, name }: { frameworkId: string; name: string }) {
  const styles = useStyles();
  const api = useApi();
  const guides = useAsync(() => api.getGuides(), []);
  const guide = guides.data?.guides?.[frameworkId];

  return (
    <Popover withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          size="small"
          appearance="transparent"
          icon={<Info20Regular />}
          aria-label={`How to read ${name}`}
        />
      </PopoverTrigger>

      <PopoverSurface>
        <div className={styles.body}>
          <Text weight="semibold" size={400}>{name}</Text>

          {guides.loading && <Spinner size="tiny" label="Loading" />}

          {!guides.loading && !guide && (
            <Text className={styles.muted}>
              No reading guidance has been written for this framework yet.
            </Text>
          )}

          {guide && (
            <>
              <Text size={300}>{guide.summary}</Text>

              <div>
                <div className={styles.label}>How to read it</div>
                <Text size={300}>{guide.howToRead}</Text>
              </div>

              {guide.groupings.length > 0 && (
                <div>
                  <div className={styles.label}>What the labels mean</div>
                  <div className={styles.term}>
                    {guide.groupings.map((g) => (
                      <div key={g.label} style={{ display: 'contents' }}>
                        <span className={styles.key}>{g.label}</span>
                        <Text size={200}>{g.meaning}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guide.caution && (
                <MessageBar intent="warning">
                  <MessageBarBody>{guide.caution}</MessageBarBody>
                </MessageBar>
              )}
            </>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
